var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
export const ACTIVE_ORGANIZATION_STORAGE_KEY = 'activeOrganizationId';
/**
 * localStorage keys whose values belong to one organization (unread counters from
 * unreadCountPersistence.ts, the orders dashboard filters). They are dropped whenever the active
 * organization changes, so nothing from the previous organization survives the switch.
 */
export const ORGANIZATION_SCOPED_STORAGE_KEYS = [
    'inbox_unread_counts',
    'inbox_unread_message_ids',
    'inbox_total_unread_count',
    'orders_filters',
];
/** HTTP status the backend answers with when the requested organization cannot be used. */
const ORGANIZATION_NOT_ACCESSIBLE_STATUS = 409;
/**
 * The organization the user is currently working in.
 *
 * Persisted in localStorage so a page reload keeps the selection, and sent on every request as
 * `X-Organization-Id`. It is only ever a *request*: the backend re-verifies that the caller is
 * an active member of that organization, and rejects the call otherwise. Nothing here grants
 * access, so a tampered value gains nothing.
 */
export function getActiveOrganizationId() {
    try {
        return localStorage.getItem(ACTIVE_ORGANIZATION_STORAGE_KEY);
    }
    catch (_a) {
        // Private browsing can make localStorage throw; falling back to the user's default
        // organization is better than failing the request.
        return null;
    }
}
export function setActiveOrganizationId(organizationId) {
    try {
        if (organizationId) {
            localStorage.setItem(ACTIVE_ORGANIZATION_STORAGE_KEY, organizationId);
        }
        else {
            localStorage.removeItem(ACTIVE_ORGANIZATION_STORAGE_KEY);
        }
    }
    catch (_a) {
        // Ignore: the selection simply will not survive a reload.
    }
}
/** Indirection so tests can observe the reload without a real navigation. */
export const navigation = {
    reload: () => {
        window.location.assign(process.env.MAIN_PATH || '/');
    },
};
function clearOrganizationScopedKeys() {
    try {
        ORGANIZATION_SCOPED_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
    }
    catch (_a) {
        // Ignore: storage that cannot be read cannot leak anything either.
    }
}
/** Forgets the active organization and everything stored for it (logout, refused org). */
export function clearOrganizationState() {
    setActiveOrganizationId(null);
    clearOrganizationScopedKeys();
}
/**
 * Moves the user to another organization.
 *
 * Each organization's data lives in its own database schema and ids repeat between them, so a
 * switch is a hard boundary: the page reloads, which drops the Apollo cache, every Redux slice,
 * open sidebars and modals, and reconnects the WebSocket for the new organization. The token is
 * refreshed first so its `organization` claim includes an organization joined since sign-in.
 */
export function switchOrganization(organizationId, refreshToken) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            yield refreshToken();
        }
        catch (_a) {
            // An unrefreshed token only matters for a just-joined organization; the backend refuses
            // that case and the recovery below brings the user back to an organization they can use.
        }
        clearOrganizationScopedKeys();
        setActiveOrganizationId(organizationId);
        navigation.reload();
    });
}
/**
 * The backend refused the stored organization (deleted, user removed, not ready). Dropping it
 * and reloading lets the backend pick the user's first available organization, instead of the
 * app failing on every request.
 */
export function recoverFromInaccessibleOrganization() {
    clearOrganizationState();
    navigation.reload();
}
export function isOrganizationNotAccessible(status) {
    return status === ORGANIZATION_NOT_ACCESSIBLE_STATUS;
}
/**
 * Keeps every open tab on the same organization: when another tab switches, this one reloads
 * instead of sending the new header while still showing the previous organization's data.
 * Returns a function that stops watching.
 */
export function watchActiveOrganization() {
    const onStorage = (event) => {
        if (event.key === ACTIVE_ORGANIZATION_STORAGE_KEY && event.newValue !== event.oldValue) {
            navigation.reload();
        }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
}
export const ACTIVE_ORGANIZATION_HEADER = 'X-Organization-Id';
export default { getActiveOrganizationId, setActiveOrganizationId };
